import { Component } from '@theme/component';

/* ↗ .cursor/references/code-notes/r-store-location.js.md#§1 */

/** Minimum time the checkbox animation is shown before the email appears. */
const REVEAL_MIN_MS = 500;

/* ↗ .cursor/references/code-notes/r-store-location.js.md#§2 */
class RStoreGallery extends Component {
  #index = 0;
  #abort = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abort;

    this.addEventListener('click', this.#handleClick, { signal });

    const dialog = this.querySelector('dialog');
    if (dialog) dialog.addEventListener('keydown', this.#handleKeydown, { signal });

    this.#show(0);
  }

  disconnectedCallback() {
    this.#abort.abort();
    super.disconnectedCallback();
  }

  get #slides() {
    return this.querySelectorAll('[data-gallery-slide]');
  }

  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const opener = target.closest('[data-gallery-open]');
    if (opener) {
      this.#show(Number(opener.dataset.galleryOpen) || 0);
      this.#openDialog();
      return;
    }

    if (target.closest('[data-gallery-prev]')) this.#step(-1);
    else if (target.closest('[data-gallery-next]')) this.#step(1);
  };

  #handleKeydown = (event) => {
    if (event.key === 'ArrowLeft') this.#step(-1);
    else if (event.key === 'ArrowRight') this.#step(1);
  };

  #openDialog() {
    const id = this.dataset.dialogId;
    const dialogComponent = id ? document.getElementById(id) : this.querySelector('dialog-component');
    dialogComponent?.showDialog?.();
  }

  #step(delta) {
    this.#show(this.#index + delta);
  }

  #show(next) {
    const slides = this.#slides;
    if (!slides.length) return;

    this.#index = ((next % slides.length) + slides.length) % slides.length;

    slides.forEach((slide, i) => {
      slide.hidden = i !== this.#index;
    });

    const counter = this.querySelector('[data-gallery-counter]');
    if (counter) counter.textContent = `${this.#index + 1} / ${slides.length}`;
  }
}

/* ↗ .cursor/references/code-notes/r-store-location.js.md#§3 */
class RStoreEmail extends Component {
  requiredRefs = ['button', 'result', 'link'];

  #abort = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.refs.button.addEventListener('click', this.#reveal, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
    super.disconnectedCallback();
  }

  #reveal = async () => {
    const { button, box, result, link, error } = this.refs;
    if (button.disabled) return;

    button.disabled = true;
    box?.classList.add('is-checked');
    if (error) error.hidden = true;

    const [email] = await Promise.all([
      this.#fetchEmail(),
      new Promise((resolve) => setTimeout(resolve, REVEAL_MIN_MS)),
    ]);

    if (!email) {
      button.disabled = false;
      box?.classList.remove('is-checked');
      if (error) error.hidden = false;
      return;
    }

    // textContent / setAttribute, never innerHTML — the value comes off the network.
    link.textContent = email;
    link.href = `mailto:${email}`;
    result.hidden = false;
    button.hidden = true;
  };

  /* ↗ .cursor/references/code-notes/r-store-location.js.md#§4 */
  async #fetchEmail() {
    const base = this.dataset.apiUrl?.replace(/\/+$/, '');
    const handle = this.dataset.handle;
    if (!base || !handle) return null;

    const candidates = [
      `${base}/stores/${encodeURIComponent(handle)}/email`,
      `${base}/?action=reveal-email&handle=${encodeURIComponent(handle)}`,
    ];

    for (const url of candidates) {
      try {
        const response = await fetch(url, { signal: this.#abort.signal });
        if (!response.ok) continue;

        const data = await response.json();
        const email = data?.email ?? data?.store?.email;
        if (email) return email;
      } catch {
        // Network error or aborted — fall through to the next candidate.
      }
    }

    return null;
  }
}

if (!customElements.get('r-store-gallery')) {
  customElements.define('r-store-gallery', RStoreGallery);
}

if (!customElements.get('r-store-email')) {
  customElements.define('r-store-email', RStoreEmail);
}
